import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "Can I add my spouse to the title of our home?",
    a: "Yes. It's done with a transfer lodged with Titles Queensland. Where the property is your home and your spouse becomes a joint owner, a transfer duty exemption may apply, so you may pay little or no duty. The rules are specific, so we check you qualify before anything is lodged.",
  },
  {
    q: "Can I take someone's name off the title?",
    a: "Yes, but only with their agreement (or a court order). The person coming off the title is transferring their share to you, so it is treated like a sale of that share for duty, tax and lending purposes.",
  },
  {
    q: "Do I have to pay stamp duty on a transfer to a family member?",
    a: "Usually duty is worked out on the market value of the share being transferred, even if no money changes hands. There are exemptions for some spouse transfers and for transfers made under family law orders or financial agreements after a separation. Getting this wrong is the most expensive mistake in family transfers.",
  },
  {
    q: "What about the mortgage?",
    a: "If there's a loan over the property, the bank has to agree. Removing a borrower usually means the remaining owner refinancing in their own name. Talk to your lender early.",
  },
  {
    q: "Is there capital gains tax?",
    a: "A transfer of your main residence is often CGT-free, but investment properties or partly rented homes can trigger tax. We'll tell you when to involve your accountant.",
  },
  {
    q: "We've separated. How do we transfer the house?",
    a: "Transfers under family court consent orders or a binding financial agreement are usually exempt from transfer duty, but the paperwork has to line up exactly. We handle the conveyancing side and work with your family lawyer.",
  },
  {
    q: "Should I think about my will at the same time?",
    a: "Yes. Changing how a property is owned (joint tenants versus tenants in common) changes who gets it when you die. We'll make sure your title and your will say the same thing.",
  },
];

export const Route = createFileRoute("/transfer-property-to-family-qld")({
  head: () => ({
    meta: [
      { title: fitTitle("Transfer Property to Spouse or Family QLD | Coastside Law") },
      {
        name: "description",
        content:
          fitDescription("Adding a spouse to title, taking a name off, or transferring to family in Queensland: stamp duty exemptions, mortgages, CGT and separation transfers. Solicitor-led, fixed fee."),
      },
      { property: "og:title", content: fitTitle("Transferring property to a spouse or family member in Queensland") },
      {
        property: "og:description",
        content: fitDescription("Duty, the bank, tax and your will: what to check before you change who owns your home."),
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://coastsidelaw.com.au/transfer-property-to-family-qld" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/transfer-property-to-family-qld" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Property transfers"
      title="Transferring property to a spouse or family member in Queensland"
      lead="Adding a partner to the title, taking a name off, or passing a home to a child isn't a normal sale, and the costs hide in duty, the mortgage and tax. Here's what to check first."
      boxTitle="Check these before you transfer"
      boxItems={[
        "Transfer duty, and whether a spouse or separation exemption applies",
        "Lender consent if there's a mortgage",
        "Capital gains tax if it's not purely your home",
        "Joint tenants or tenants in common, and what that means when you die",
        "Your will, so it matches the new title",
      ]}
      faqs={FAQS}
      links={[
        { to: "/conveyancing", label: "Conveyancing" },
        { to: "/wills-and-estates", label: "Wills & estates" },
        { to: "/stamp-duty-calculator-qld", label: "Stamp duty calculator" },
        { to: "/guides/joint-tenants-vs-tenants-in-common-queensland", label: "Joint tenants or tenants in common" },
      ]}
      formHeading="Tell us about the transfer"
      ctaTitle="Changing who owns your home?"
    />
  ),
});
